import { create } from 'zustand';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import useBroadcastsVerbalMentions from './useBroadcastsVerbalMentions';
import getFilters from '../../selectors/getSelectedFilters';
import { VerbalMentionRsp, VerbalMentions } from '../../types/api';
import * as format from '../../utils/formatters';
import Papa from 'papaparse';
import downloadCSV from '../../utils/downloadCsv';

const VM_PAGE_SIZE = 10;

const generateVerbalMentionKey = (mention: VerbalMentionData) => {
    return `${mention.mentionId}-${mention.network.id}-${mention.broadcastName}`;
};

interface VerbalMentionData extends VerbalMentionRsp {
    contentDate: string;
}

const flattenVerbalMentions = (
    verbalExposures: VerbalMentions[] = [],
): VerbalMentionData[] =>
    verbalExposures.flatMap(({ broadcastRsps = [], contentDate }) =>
        broadcastRsps.map(vm => ({
            ...vm,
            contentDate,
        })),
    );

type State = {
    verbalMentionsData: Map<string, VerbalMentionData>;
    isLoadingVerbalMentions: boolean;
    isFetchingVerbalMentions: boolean;
    currentPage: number;
    totalVerbalMentions: number;
    isErrorFetchingVerbalMentions: boolean;
};

type Actions = {
    setVerbalMentionsData: (newVerbalMentionsData: VerbalMentionData[]) => void;
    setIsLoadingVerbalMentions: (isLoading: boolean) => void;
    setIsFetchingVerbalMentions: (isFetching: boolean) => void;
    setCurrentPage: (page: number) => void;
    setTotalVerbalMentions: (total: number) => void;
    setIsErrorFetchingVerbalMentions: (isError: boolean) => void;
};

const useVerbalMentionsTableStore = create<State & Actions>(set => ({
    verbalMentionsData: new Map(),
    isLoadingVerbalMentions: false,
    isFetchingVerbalMentions: false,
    currentPage: 0,
    totalVerbalMentions: 0,
    isErrorFetchingVerbalMentions: false,
    setVerbalMentionsData: (newVerbalMentionsData: VerbalMentionData[]) => {
        const verbalMentionsData = new Map<string, VerbalMentionData>();
        newVerbalMentionsData.forEach(vm => {
            verbalMentionsData.set(generateVerbalMentionKey(vm), vm);
        });

        set({ verbalMentionsData: new Map(verbalMentionsData) });
    },
    setIsLoadingVerbalMentions: (isLoading: boolean) => {
        set({ isLoadingVerbalMentions: isLoading });
    },
    setIsFetchingVerbalMentions: (isFetching: boolean) => {
        set({ isFetchingVerbalMentions: isFetching });
    },
    setCurrentPage: (page: number) => {
        set({ currentPage: page });
    },
    setTotalVerbalMentions: (total: number) => {
        set({ totalVerbalMentions: total });
    },
    setIsErrorFetchingVerbalMentions: (isError: boolean) => {
        set({ isErrorFetchingVerbalMentions: isError });
    },
}));

const useSetUpVerbalMentionsTable = (
    filters: any,
    attributionQueryFields: Record<string, boolean> = {},
) => {
    const {
        setVerbalMentionsData,
        setIsLoadingVerbalMentions,
        setIsFetchingVerbalMentions,
        setIsErrorFetchingVerbalMentions,
        currentPage,
        setCurrentPage,
        setTotalVerbalMentions,
    } = useVerbalMentionsTableStore();

    const globFilters: any = useMemo(() => getFilters(filters), [filters]);

    // Make sure to reset the current page when the filters change
    useEffect(() => {
        setCurrentPage(0);
    }, [globFilters]);

    const verbalMentionsQuery = useMemo(() => {
        return {
            ...globFilters,
            ...attributionQueryFields,
            page: currentPage,
            pageSize: VM_PAGE_SIZE,
            orderBy: ['contentDate-desc'],
        };
    }, [globFilters, currentPage, attributionQueryFields]);

    const {
        data: fetchedVerbalMentions,
        isLoading: isLoadingVerbalMentions,
        isFetching: isFetchingVerbalMentions,
        isError: isErrorFetchingVerbalMentions,
    } = useBroadcastsVerbalMentions(verbalMentionsQuery);

    useEffect(() => {
        setIsLoadingVerbalMentions(isLoadingVerbalMentions);
        setIsFetchingVerbalMentions(isFetchingVerbalMentions);
        if (!isLoadingVerbalMentions && fetchedVerbalMentions) {
            const transformedData = flattenVerbalMentions(
                fetchedVerbalMentions.verbalExposures,
            );

            setVerbalMentionsData(transformedData);
            setTotalVerbalMentions(fetchedVerbalMentions.total ?? 0);
        }
    }, [
        isLoadingVerbalMentions,
        fetchedVerbalMentions,
        isFetchingVerbalMentions,
    ]);

    useEffect(() => {
        setIsErrorFetchingVerbalMentions(isErrorFetchingVerbalMentions);
    }, [isErrorFetchingVerbalMentions]);
};

const useGetVerbalMentionsCsv = (
    enableDownload: boolean,
    setEnableDownload: (enable: boolean) => void,
    delimiter: string,
    filters: any,
    sendNotification: (message: string, config?: any) => void,
    t: (key: string) => string,
    attributionQueryFields: Record<string, boolean> = {},
) => {
    const globFilters: any = useMemo(() => getFilters(filters), [filters]);

    const verbalMentionsQuery = useMemo(() => {
        return {
            ...globFilters,
            ...attributionQueryFields,
            orderBy: ['contentDate-desc'],
        };
    }, [globFilters, attributionQueryFields]);

    const {
        isFetching: isLoading,
        data: fetchedVerbalMentions,
        isError: isErrorDownloadingVerbalMentions,
    } = useBroadcastsVerbalMentions(verbalMentionsQuery, true, enableDownload);

    const verbalMentionsCsvData = useMemo(
        () =>
            isLoading
                ? []
                : flattenVerbalMentions(
                      fetchedVerbalMentions.verbalExposures,
                  ).map(vm => ({
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.event',
                      )]: vm.broadcastName,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.sponsor',
                      )]: vm.sponsor.name,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.keyWord',
                      )]: vm.mention,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.mvpPercentage',
                      )]: format.percentage(vm.averageMvpScore),
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.smv',
                      )]: format.shortCurrency(vm.sponsorMediaValue, 1),
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.network',
                      )]: vm.network.name,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.durationSeconds',
                      )]: vm.duration,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.impressions',
                      )]: vm.impressions,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.startTimeSeconds',
                      )]: vm.startAt,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.endTimeSeconds',
                      )]: vm.endAt,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.context',
                      )]: vm.context,
                      [t(
                          'measurement:broadcastExposure.verbalMentions.table.columns.contentDate',
                      )]: moment(vm.contentDate).format('YYYY/MM/DD'),
                  })),
        [fetchedVerbalMentions, isLoading],
    );

    useEffect(() => {
        if (enableDownload && !isLoading) {
            if (verbalMentionsCsvData.length > 0) {
                const csv = Papa.unparse(verbalMentionsCsvData, {
                    delimiter: delimiter || ',',
                });

                downloadCSV(csv, 'verbal-mentions', true);

                sendNotification(
                    'Verbal mentions CSV downloaded successfully',
                    {
                        context: 'success',
                    },
                );
            }
            setEnableDownload(false);
        }
    }, [isLoading, fetchedVerbalMentions, enableDownload]);

    return { isLoading, isErrorDownloadingVerbalMentions };
};

export {
    VM_PAGE_SIZE,
    generateVerbalMentionKey,
    VerbalMentionData,
    useVerbalMentionsTableStore,
    useSetUpVerbalMentionsTable,
    useGetVerbalMentionsCsv,
};
